import React, { useMemo, useEffect, useState } from "react";
import SponsorContainer from "./HomePage/SponsorContainer";
import "./SponsorScroller.css";

interface Sponsor {
  name: string;
  image: string;
}

interface SponsorScrollerProps {
  sponsors: Sponsor[];
  baseAnimationDuration?: number; // Base duration in seconds
  mobileBaseAnimationDuration?: number; // Base duration for mobile in seconds
}

export default function SponsorScroller({
  sponsors,
  baseAnimationDuration = 30,
  mobileBaseAnimationDuration = 40,
}: SponsorScrollerProps) {
  const [isMobile, setIsMobile] = useState(false);

  // Check if screen is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Create duplicated array to ensure smooth scrolling
  const createSufficientSponsors = (originalSponsors: Sponsor[]) => {
    if (originalSponsors.length === 0) return [];

    // Calculate how many copies we need to reach at least 10 sponsors for smooth scroll
    const minimumSponsorsRequired = 10;
    const repetitions = Math.ceil(minimumSponsorsRequired / originalSponsors.length);

    // Create an array with enough repetitions
    const expandedSponsors = Array(repetitions)
      .fill([...originalSponsors])
      .flat()
      .map((sponsor, index) => ({
        ...sponsor,
        uniqueKey: `${sponsor.name}-${index}`,
      }));

    return expandedSponsors;
  };

  const displaySponsors = createSufficientSponsors(sponsors);

  // Calculate animation duration based on number of sponsors
  const animationDuration = useMemo(() => {
    if (displaySponsors.length === 0)
      return isMobile ? mobileBaseAnimationDuration : baseAnimationDuration;

    // Scale the duration based on number of sponsors
    // More sponsors = longer duration to maintain consistent visual speed
    const baseDuration = isMobile
      ? mobileBaseAnimationDuration
      : baseAnimationDuration;
    return baseDuration * (displaySponsors.length / 7);
  }, [
    displaySponsors.length,
    baseAnimationDuration,
    mobileBaseAnimationDuration,
    isMobile,
  ]);

  // Apply the calculated duration as an inline style
  const slideStyle = {
    animationDuration: `${animationDuration}s`,
  };

  return (
    <div className="sponsors-scroller">
      <div className="sponsors-slide" style={slideStyle}>
        {displaySponsors.map((sponsor: any) => (
          <div
            key={sponsor.uniqueKey || `${sponsor.name}-${Math.random()}`}
            className="sponsor-card-container"
          >
            <SponsorContainer name={sponsor.name} image={sponsor.image} />
          </div>
        ))}
      </div>
      <div className="sponsors-slide" style={slideStyle}>
        {displaySponsors.map((sponsor: any) => (
          <div
            key={
              `${sponsor.uniqueKey}-duplicate` ||
              `${sponsor.name}-duplicate-${Math.random()}`
            }
            className="sponsor-card-container"
          >
            <SponsorContainer name={sponsor.name} image={sponsor.image} />
          </div>
        ))}
      </div>
    </div>
  );
}

